import { useEffect, useRef, useState } from 'react';
import { Input } from './input';
import { parseHours } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * A numeric field that keeps its own text buffer.
 *
 * A naively controlled `value={String(n)}` + `onChange={Number(...)}` input is
 * unusable: typing "9.5" re-renders as "9" the moment the "." is typed (because
 * `Number("9.") === 9` and `String(9) === "9"`), so the decimal point is eaten
 * and you end up with 95. Every numeric field in the app goes through here.
 *
 * Clearing the field is always allowed. While it is focused the buffer can be
 * empty; nothing is emitted for an empty buffer unless `allowEmpty` is set.
 * On blur an empty (or below-minimum) buffer is filled with `min` (default 0)
 * and that value is emitted, so the parent never sees a null it has to coerce
 * and the field never snaps back to a stale value while someone is typing.
 *
 * The buffer is only re-synced from the prop when the parsed value actually
 * differs, so an external update (an optimistic patch landing, say) still shows
 * through without stomping on what someone is mid-way through typing.
 */
export function NumberInput({
  value,
  onChange,
  className,
  parse = 'decimal',
  allowEmpty,
  min,
  placeholder,
  'aria-label': ariaLabel,
  size = 'md'
}: {
  value: number | null;
  onChange: (value: number | null) => void;
  className?: string;
  /** `duration` also accepts 1:30 and 90m. */
  parse?: 'decimal' | 'duration';
  /** Emit null when cleared instead of filling with `min` on blur. */
  allowEmpty?: boolean;
  /** Lower bound; an empty or smaller value becomes this on blur. Default 0. */
  min?: number;
  placeholder?: string;
  'aria-label'?: string;
  size?: 'sm' | 'md';
}) {
  const [text, setText] = useState(() => (value === null ? '' : String(value)));
  const lastEmitted = useRef<number | null>(value);
  const floor = min ?? 0;

  useEffect(() => {
    if (value !== lastEmitted.current) {
      setText(value === null ? '' : String(value));
      lastEmitted.current = value;
    }
  }, [value]);

  const parseValue = (raw: string): number | null => {
    if (raw.trim() === '') return null;
    if (parse === 'duration') return parseHours(raw);
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  const emit = (next: number | null) => {
    lastEmitted.current = next;
    onChange(next);
  };

  const parsed = parseValue(text);
  const invalid = text.trim() !== '' && parsed === null;

  return (
    <Input
      aria-label={ariaLabel}
      inputMode="decimal"
      placeholder={placeholder}
      className={cn('text-right tnum', size === 'sm' && 'h-8', className)}
      value={text}
      invalid={invalid}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        const next = parseValue(raw);
        // Unparseable text: keep the buffer, leave the value alone.
        if (next === null && raw.trim() !== '') return;
        // Cleared: only tell the parent when it opted into null.
        if (next === null && !allowEmpty) return;
        emit(next);
      }}
      onBlur={() => {
        if (parsed !== null) {
          // Normalize so "1." or "01.50" tidies itself up, clamping to the floor.
          const settled = min !== undefined && parsed < min ? min : parsed;
          setText(String(settled));
          if (settled !== lastEmitted.current) emit(settled);
          return;
        }
        if (allowEmpty) {
          if (text.trim() !== '') setText('');
          if (lastEmitted.current !== null) emit(null);
          return;
        }
        // Empty or garbage with no null allowed: fill with the floor.
        setText(String(floor));
        if (floor !== lastEmitted.current) emit(floor);
      }}
    />
  );
}
